'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { type StatementCard, pickText } from '@/components/SwipeCard';
import { countries as allCountries } from '@/data/countries';
import { countryNameIn, t } from '@/data/translations';
import { getLanguageByCode } from '@/data/languages';

interface ActiveCountry {
  code: string;
  name_en: string;
  statement_count: string | number;
  recent_count: string | number;
  recent_score: number;
  score_total: number;
}

interface ExploreBoardProps {
  langCode: string;
  onSelectStatement: (statement: StatementCard, stack: StatementCard[]) => void;
  viewMine?: boolean;
  submittedJustNow?: StatementCard | null;
}

// Lookup: country code → flag emoji
const flagByCode = new Map(allCountries.map(c => [c.code, c.flag]));
const fallbackFlag = '🌍';

function withFlag(s: Omit<StatementCard, 'flag'> & { flag?: string }): StatementCard {
  return { ...s, flag: s.flag ?? flagByCode.get(s.country_code) ?? fallbackFlag };
}

export default function ExploreBoard({ langCode, onSelectStatement, viewMine = false, submittedJustNow = null }: ExploreBoardProps) {
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  if (isDesktop === null) return null;

  if (viewMine) {
    return <MineFeed langCode={langCode} onSelectStatement={onSelectStatement} submittedJustNow={submittedJustNow} />;
  }

  return isDesktop
    ? <DesktopColumns langCode={langCode} onSelectStatement={onSelectStatement} />
    : <MobileFeed langCode={langCode} onSelectStatement={onSelectStatement} />;
}

// -------- Your posts: single feed of the current session's submissions --------

function MineFeed({ langCode, onSelectStatement, submittedJustNow }: { langCode: string; onSelectStatement: ExploreBoardProps['onSelectStatement']; submittedJustNow: StatementCard | null }) {
  const [cards, setCards] = useState<StatementCard[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/statements?mine=1&limit=100&orderBy=new', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setCards((d.statements || []).map(withFlag)); })
      .catch(() => { if (!cancelled) setCards([]); });
    return () => { cancelled = true; };
  }, [submittedJustNow?.id]);

  if (cards === null) return <Loading />;
  if (cards.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-8 text-center">
        <div className="text-5xl mb-4">✍️</div>
        <p className="text-slate-300 font-medium mb-1" lang={langCode}>You haven&apos;t written anything yet</p>
        <p className="text-slate-500 text-sm" lang={langCode}>Tap &ldquo;{t('writeYourOwn', langCode)}&rdquo; up top to add your voice.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-4 pb-6">
      <div className="max-w-2xl mx-auto grid grid-cols-1 gap-3">
        {cards.map(c => (
          <CompactCard
            key={c.id}
            card={c}
            langCode={langCode}
            onClick={() => onSelectStatement(c, cards)}
          />
        ))}
      </div>
    </div>
  );
}

// -------- Mobile: single hot feed --------

function MobileFeed({ langCode, onSelectStatement }: ExploreBoardProps) {
  const [cards, setCards] = useState<StatementCard[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/statements?orderBy=hot&limit=60')
      .then(r => r.json())
      .then(d => { if (!cancelled) setCards((d.statements || []).map(withFlag)); })
      .catch(() => { if (!cancelled) setCards([]); });
    return () => { cancelled = true; };
  }, []);

  if (cards === null) return <Loading />;
  if (cards.length === 0) return <EmptyState langCode={langCode} />;

  return (
    <div className="h-full overflow-y-auto px-4 pb-6">
      <div className="grid grid-cols-1 gap-3">
        {cards.map(c => (
          <CompactCard
            key={c.id}
            card={c}
            langCode={langCode}
            onClick={() => onSelectStatement(c, cards)}
          />
        ))}
      </div>
    </div>
  );
}

// -------- Desktop: country columns --------

function DesktopColumns({ langCode, onSelectStatement }: ExploreBoardProps) {
  const [countries, setCountries] = useState<ActiveCountry[] | null>(null);
  const [byCountry, setByCountry] = useState<Record<string, StatementCard[]>>({});

  // Load active countries
  useEffect(() => {
    let cancelled = false;
    fetch('/api/countries/active?limit=30')
      .then(r => r.json())
      .then(d => { if (!cancelled) setCountries(d.countries || []); })
      .catch(() => { if (!cancelled) setCountries([]); });
    return () => { cancelled = true; };
  }, []);

  // Load top statements per country in parallel
  useEffect(() => {
    if (!countries || countries.length === 0) return;
    let cancelled = false;
    Promise.all(
      countries.map(async c => {
        const r = await fetch(`/api/statements?country=${c.code}&orderBy=top&limit=10`);
        const d = await r.json();
        return [c.code, (d.statements || []).map(withFlag)] as const;
      })
    ).then(pairs => {
      if (!cancelled) setByCountry(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [countries]);

  // --- Click-and-drag horizontal scroll ---
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ dragging: false, startX: 0, startScroll: 0, distance: 0 });

  // --- Auto-hiding scrollbar overlay ---
  const [scrollMetrics, setScrollMetrics] = useState({ left: 0, view: 1, total: 1 });
  const [hintVisible, setHintVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thumbDragRef = useRef({ dragging: false, startX: 0, startScroll: 0 });

  const updateMetrics = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollMetrics({ left: el.scrollLeft, view: el.clientWidth, total: el.scrollWidth });
  };

  const showHint = () => {
    setHintVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHintVisible(false), 1500);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateMetrics();
    const onScroll = () => { updateMetrics(); showHint(); };
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => updateMetrics();
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [countries, byCountry]);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    dragState.current = {
      dragging: true,
      startX: e.pageX,
      startScroll: scrollRef.current.scrollLeft,
      distance: 0,
    };
    if (scrollRef.current) scrollRef.current.style.cursor = 'grabbing';
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    showHint();
    if (!dragState.current.dragging || !scrollRef.current) return;
    const dx = e.pageX - dragState.current.startX;
    dragState.current.distance = Math.max(dragState.current.distance, Math.abs(dx));
    scrollRef.current.scrollLeft = dragState.current.startScroll - dx;
  };

  const endDrag = () => {
    dragState.current.dragging = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
    setTimeout(() => { dragState.current.distance = 0; }, 50);
  };

  // Thumb-drag: clicking and dragging the scrollbar thumb directly.
  const onThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!scrollRef.current) return;
    thumbDragRef.current = { dragging: true, startX: e.pageX, startScroll: scrollRef.current.scrollLeft };
    showHint();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!thumbDragRef.current.dragging || !scrollRef.current) return;
      const dx = e.pageX - thumbDragRef.current.startX;
      // Map thumb-track distance to content scroll: thumb moves through (clientWidth - thumbWidth)
      // while content scrolls (scrollWidth - clientWidth). Ratio = (scrollWidth/clientWidth)-ish.
      const el = scrollRef.current;
      const ratio = el.scrollWidth / el.clientWidth;
      el.scrollLeft = thumbDragRef.current.startScroll + dx * ratio;
    };
    const onUp = () => { thumbDragRef.current.dragging = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const wrappedSelect = (s: StatementCard, stack: StatementCard[]) => {
    if (dragState.current.distance > 5) return;
    onSelectStatement(s, stack);
  };

  if (countries === null) return <Loading />;
  if (countries.length === 0) return <EmptyState langCode={langCode} />;

  const thumbWidthPct = Math.min(100, (scrollMetrics.view / scrollMetrics.total) * 100);
  const thumbLeftPct = (scrollMetrics.left / Math.max(1, scrollMetrics.total - scrollMetrics.view)) * (100 - thumbWidthPct);
  const isScrollable = scrollMetrics.total > scrollMetrics.view + 4;

  return (
    <div className="relative h-full">
      <div
        ref={scrollRef}
        className="h-full overflow-x-auto overflow-y-hidden px-4 pb-4 select-none [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', cursor: 'grab' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <div className="flex gap-4 h-full items-stretch">
          {countries.map(c => (
            <CountryColumn
              key={c.code}
              country={c}
              cards={byCountry[c.code] || []}
              langCode={langCode}
              onSelectStatement={wrappedSelect}
            />
          ))}
        </div>
      </div>

      {/* Auto-hiding scrollbar hint */}
      {isScrollable && (
        <div
          className={`pointer-events-none absolute bottom-1 left-4 right-4 h-2 transition-opacity duration-300 ${
            hintVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="relative h-full bg-slate-800/60 rounded-full backdrop-blur-sm">
            <div
              className="pointer-events-auto absolute top-0 h-full bg-slate-500/90 hover:bg-slate-400 rounded-full cursor-grab active:cursor-grabbing"
              style={{ left: `${thumbLeftPct}%`, width: `${thumbWidthPct}%` }}
              onMouseDown={onThumbMouseDown}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function CountryColumn({
  country,
  cards,
  langCode,
  onSelectStatement,
}: {
  country: ActiveCountry;
  cards: StatementCard[];
  langCode: string;
  onSelectStatement: (s: StatementCard, stack: StatementCard[]) => void;
}) {
  const flag = flagByCode.get(country.code) ?? fallbackFlag;
  const localizedName = countryNameIn(country.code, langCode, true);
  const recentScore = country.recent_score || 0;
  const totalScore = country.score_total || 0;
  const heat = recentScore || totalScore;

  return (
    <div className="w-72 shrink-0 flex flex-col bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden">
      {/* Column header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 shrink-0">
        <span className="text-2xl">{flag}</span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate" lang={langCode}>{localizedName}</div>
          <div className="text-slate-500 text-xs">
            {country.statement_count} {Number(country.statement_count) === 1 ? 'voice' : 'voices'}
            {heat > 0 && ` · ${heat} ↑`}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {cards.length === 0
          ? <div className="text-slate-600 text-xs text-center py-8">…</div>
          : cards.map(c => (
              <CompactCard
                key={c.id}
                card={c}
                langCode={langCode}
                onClick={() => onSelectStatement(c, cards)}
                dense
              />
            ))}
      </div>
    </div>
  );
}

// -------- Card --------

function CompactCard({
  card,
  langCode,
  onClick,
  dense = false,
}: {
  card: StatementCard;
  langCode: string;
  onClick: () => void;
  dense?: boolean;
}) {
  // Pick the right translation: native original if user speaks the card's language,
  // pre-translated text if available, English fallback otherwise.
  const displayText = useMemo(() => pickText(card, langCode), [card, langCode]);
  // The language we're actually rendering in (for `lang=` attribute, font shaping).
  const displayLang = useMemo(() => {
    if (card.language_code === langCode) return card.language_code;
    if (card.text_translations?.[langCode]) return langCode;
    return card.text_english ? 'en' : card.language_code;
  }, [card, langCode]);

  const langInfo = getLanguageByCode(card.language_code);
  const score = card.likes + card.super_likes;

  // Visual treatment differs for user's own posts (subtle accent), and seed
  // examples get a small "Example" pill so they don't read as real submissions.
  const isMine = card.is_mine === true;
  const isSeed = card.is_seed === true;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl transition-colors border ${
        dense ? 'p-3' : 'p-4'
      } ${
        isMine
          ? 'bg-sky-500/10 border-sky-500/40 hover:bg-sky-500/15 active:bg-sky-500/20'
          : 'bg-slate-800/80 hover:bg-slate-800 active:bg-slate-700 border-slate-700/50 hover:border-slate-600'
      }`}
    >
      {!dense && (
        <div className="flex items-center justify-between mb-2 text-xs gap-2">
          <span className="flex items-center gap-1.5 text-slate-400 min-w-0">
            <span>{card.flag}</span>
            <span lang={langCode} className="truncate">{countryNameIn(card.country_code, langCode, true)}</span>
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            {isMine && (
              <span className="text-[10px] font-semibold text-sky-300 bg-sky-500/20 px-1.5 py-0.5 rounded-full uppercase tracking-wide">You</span>
            )}
            {isSeed && (
              <span className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded-full">Example</span>
            )}
            {langInfo && (
              <span className="text-slate-500" title={langInfo.nameEn}>
                {langInfo.code.toUpperCase()}
              </span>
            )}
          </span>
        </div>
      )}
      {dense && (isMine || isSeed) && (
        <div className="flex items-center gap-1 mb-1.5">
          {isMine && <span className="text-[9px] font-semibold text-sky-300 bg-sky-500/20 px-1.5 py-0.5 rounded-full uppercase">You</span>}
          {isSeed && <span className="text-[9px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded-full">Example</span>}
        </div>
      )}

      <p
        className={`text-white leading-snug ${dense ? 'text-sm line-clamp-4' : 'text-base line-clamp-5'}`}
        lang={displayLang}
      >
        {displayText}
      </p>

      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
        {dense && (
          <span className="text-slate-600" title={langInfo?.nameEn || ''}>{card.language_code.toUpperCase()}</span>
        )}
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
          </svg>
          {score}
        </span>
      </div>
    </button>
  );
}

// -------- Helpers --------

function Loading() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-slate-500 text-sm">…</div>
    </div>
  );
}

function EmptyState({ langCode }: { langCode: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center">
      <div className="text-5xl mb-4">🌐</div>
      <p className="text-slate-400 text-sm" lang={langCode}>{t('emptyLang', langCode)}</p>
    </div>
  );
}
